<template>
  <div class="wrapper">
    <dashboard-bar refreshButton />

    <div class="body">
      <div class="header">
        <span class="header-icon">
          <font-awesome-icon :icon="['fas', 'th-list']" />
        </span>
        <h2>{{ $t('actionList.pageTitle') }}</h2>
        <span
          class="ml-3 refresh-icon"
          :title="$t('actionList.updateList')"
          @click="updateTable"
        >
          <font-awesome-icon :icon="['fas', 'sync-alt']" />
        </span>
        <router-link
          class="ml-auto"
          :to="`/new-action`"
          v-if="userProfile.permissions.actions.create"
        >
          <button class="btn btn-outline-primary">
            {{ $t('actionList.newActionButton') }}
          </button>
        </router-link>
      </div>
      <div class="row">
        <div class="col-3 mt-3">
          <div class="search-actions">
            <input
              type="text"
              class="form-control"
              id="input-action"
              :placeholder="$t('actionList.searchInput')"
              v-model="filter"
              @keyup="searchByFilter"
            />
            <button @click="cleanFilter" class="btn btn-outline-primary">
              {{ $t('actionList.searchButton') }}
            </button>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-12">
          <b-table
            striped
            responsive
            id="table"
            class="mt-4"
            :items="actionList"
            :fields="fields"
            @sort-changed="handleSorting"
            no-local-sorting
            :busy="loading"
            v-if="userProfile.permissions.actions.menu_list"
          >
            <template v-slot:table-busy>
              <div class="text-center text-primary my-2">
                <b-spinner type="grow" class="align-middle"></b-spinner>
              </div>
            </template>
            <!-- Ação -->
            <template v-slot:cell(name)="row">
              <span>{{ row.item.name }}</span> <br />
              <span
                v-if="row.item.surveyType === 'resurvey'"
                class="survey-type"
                >Tipo: Repesquisa</span
              >
              <span v-else class="survey-type">Tipo: Pesquisa</span>
            </template>
            <!-- Grupo -->
            <template v-slot:cell(groupIdList)="row">
              <span
                v-if="row.item.groupIdList.length > 1"
                style="display: flex"
              >
                {{ row.item.groupIdList[0].name }}
                <div class="groupListStyle">
                  + {{ row.item.groupIdList.length - 1 }}
                </div>
              </span>
              <span v-else-if="row.item.groupIdList.length === 1">
                {{ row.item.groupIdList[0].name }}
              </span>
              <span v-else>
                {{ $t('actionList.actionTable.noClassifiedLabel') }}
              </span>
            </template>
            <!-- Canal -->
            <template v-slot:cell(type)="row"
              >{{
                row.item.type === 'nps-0-10'
                  ? $t('actionList.nps010')
                  : row.item.type === 'nps-1-5'
                  ? $t('actionList.nps15')
                  : row.item.type === 'ces-1-5'
                  ? $t('actionList.ces15')
                  : row.item.type === 'csat-1-5'
                  ? $t('actionList.csat15')
                  : row.item.type === 'csat-1-5(media)'
                  ? $t('actionList.csat15Average')
                  : row.item.type === 'csat-0-10(media)'
                  ? $t('actionList.csat0to10Average')
                  : row.item.type === '5-stars'
                  ? $t('actionList.fiveStars')
                  : $t('actionList.likeDislike')
              }}
            </template>
            <!-- Data de criação -->
            <template v-slot:cell(createdAt)="row">{{
              dateFormat(new Date(row.item.createdAt))
            }}</template>
            <!-- Canal -->
            <template v-slot:cell(channel)="row">{{
              row.item.channel === 'emailsms'
                ? $t('actionList.both')
                : row.item.channel
            }}</template>
            <!-- Registros -->
            <template v-slot:cell(clientsCount)="row">
              {{ row.item.records }}
            </template>
            <!-- Enviados -->
            <template v-slot:cell(invites)="row">
              {{ row.item.valids }}
            </template>
            <!-- Falhas -->
            <template v-slot:cell(failures)="row">
              {{ row.item.failures }}
            </template>
            <!-- Respostas -->
            <template v-slot:cell(answers)="row">
              {{ row.item.answered }}
            </template>
            <!-- Status -->
            <template v-slot:cell(status)="row">
              <div
                :title="showSubmitDate(row.item)"
                :class="`flag-status flag-status-${row.item.status}`"
              >
                {{ $t(`actionList.actionTable.status.${row.item.status}`) }}
              </div>
            </template>
            <!-- Opções -->
            <template v-slot:cell(options)="row">
              <div class="table-icons icons" style="min-width: 140px">
                <span
                  :title="$t('actionList.details')"
                  class="table-icons"
                  v-if="userProfile.permissions.actions.details"
                >
                  <router-link :to="`/action-list/${row.item._id}`">
                    <font-awesome-icon :icon="['fas', 'search']" />
                  </router-link>
                </span>
                <span
                  :title="$t('actionList.edit')"
                  class="table-icons"
                  v-if="userProfile.permissions.actions.edit"
                >
                  <a @click="$router.push(`/action/layout/${row.item._id}`)">
                    <font-awesome-icon :icon="['fas', 'pen']" />
                  </a>
                </span>
                <span
                  :title="$t('actionList.send')"
                  class="table-icons"
                  v-if="
                    userProfile.permissions.actions.submit &&
                    row.item.channel == 'qrcode'
                  "
                >
                  <a
                    @click="
                      $router.push(`/action/prepare-qr-code/${row.item._id}`)
                    "
                  >
                    <font-awesome-icon :icon="['fas', 'qrcode']" />
                  </a>
                </span>
                <span
                  :title="$t('actionList.send')"
                  class="table-icons"
                  v-else-if="userProfile.permissions.actions.submit"
                >
                  <a
                    v-if="row.item.channel === 'link' || row.item.channel === 'totem'"
                    @click="$router.push(`/action/link/${row.item._id}`)"
                  >
                    <font-awesome-icon :icon="['fas', 'paper-plane']" />
                  </a>

                  <a
                    v-else
                    @click="$router.push(`/action/importation/${row.item._id}`)"
                  >
                    <font-awesome-icon :icon="['fas', 'paper-plane']" />
                  </a>
                </span>
                <DropdownGroup
                  style="margin-left: -6px; margin-right: -12px"
                  v-if="userProfile.permissions.action_groups.associate_action"
                  :action="row.item"
                  :groupIdList="row.item.groupIdList"
                />

                <span
                  v-if="
                    row.item.wppConfig &&
                    row.item.wppConfig.templateName !== undefined
                  "
                  title="Whatsapp ativo na ação"
                  class="table-icons"
                >
                  <i
                    class="fab fa-whatsapp-square mt-2"
                    style="font-size: 24px"
                  ></i>
                </span>

                <b-button
                  style="background-color: transparent; border: transparent"
                  v-b-modal="`modal-config-${row.item._id}`"
                  @click="openModal(row.item)"
                >
                  <i class="text-primary fas fa-ellipsis-h"></i
                ></b-button>

                <b-modal
                  size="lg"
                  :id="`modal-config-${row.item._id}`"
                  title="Opções"
                >
                  <template #button-content>
                    <i class="text-primary fas fa-ellipsis-h"></i>
                  </template>
                  <p
                    style="
                      margin-left: 8px;
                      color: #606060;
                      font-size: 1.2em;
                      -webkit-box-shadow: none;
                      box-shadow: none;
                      margin-bottom: 5px;
                    "
                  >
                    <span v-if="userProfile.permissions.actions.create">
                      <a @click="cloneAction(row.item._id)">
                        <i class="table-icons fas fa-copy"></i>
                        <span class="text-config">{{
                          $t('actionTranslateUpdate.options.clone')
                        }}</span>
                      </a>
                    </span>
                  </p>

                  <p>
                    <DropdownCategoryGroups
                      style="
                        margin-left: -6px;
                        margin-right: -12px;
                        margin-bottom: -10px;
                      "
                      v-if="
                        userProfile.permissions.action_groups.associate_action
                      "
                      :action="row.item"
                      :groupIdList="row.item.groupIdList"
                    />
                  </p>

                  <p>
                    <DropdownTagGroups
                      style="
                        margin-left: -6px;
                        margin-right: -12px;
                        margin-bottom: -10px;
                      "
                      :action="row.item"
                      :groupIdList="row.item.groupIdList"
                    />
                  </p>

                  <p
                    class="align-paragraph"
                    v-if="userProfile.permissions.actions.delete"
                  >
                    <a @click="remove(row.item._id)">
                      <i class="table-icons fas fa-trash-alt"> </i>
                      <span class="text-config">{{
                        $t('actionTranslateUpdate.options.removeAction')
                      }}</span>
                    </a>
                  </p>
                  <p class="align-paragraph">
                    <span>
                      <a @click="showWebhook = !showWebhook">
                        <i class="table-icons fas fa-link"></i>
                        <span class="text-config">
                          {{ $t('actionList.registerWebhook') }}
                        </span>
                      </a>
                    </span>
                  </p>
                  <div style="margin-bottom: 20px" v-if="showWebhook">
                    <div class="form-group">
                      <label> HTTP Post URL: </label>
                      <div class="display-flex">
                        <input
                          type="text"
                          :id="`webhook-${row.index}`"
                          class="form-control"
                          v-model="row.item.linkWebhook"
                        />

                        <button
                          @click="
                            saveWebhook(row.item._id, row.item.linkWebhook)
                          "
                          class="btn btn-primary"
                          style="max-width: 300px"
                        >
                          {{ $t('actionList.saveWebhook') }}
                        </button>
                      </div>

                      <label>{{ $t('actionList.required') }}</label>
                    </div>
                    <div class="display-flex">
                      <button
                        @click="
                          testIntegrationAnswer(
                            row.item._id,
                            row.item.linkWebhook,
                          )
                        "
                        class="btn btn-primary"
                      >
                        {{ $t('actionList.testIntegration') }}
                      </button>

                      <b-form-checkbox
                        style="margin-top: 10px; margin-left: 10px"
                        :id="`enable-config-${row.index}`"
                        name="check-button"
                        v-model="row.item.activeWebhook"
                        @change="
                          activateWebhook(row.item._id, row.item.activeWebhook)
                        "
                        switch
                      >
                        {{ $t('actionList.habilitIntegration') }}
                      </b-form-checkbox>
                    </div>
                  </div>

                  <p class="align-paragraph">
                    <span>
                      <a @click="showWebhookShoot = !showWebhookShoot">
                        <i class="table-icons fas fa-link"></i>
                        <span class="text-config">
                          Cadastrar webhook por disparo
                        </span>
                      </a>
                    </span>
                  </p>
                  <div style="margin-bottom: 20px" v-if="showWebhookShoot">
                    <div class="form-group">
                      <label> Webhook URL: </label>
                      <div class="display-flex">
                        <input
                          type="text"
                          :id="`webhook-shoot-${row.index}`"
                          class="form-control"
                          v-model="row.item.linkWebhookSubmit"
                        />

                        <button
                          @click="
                            saveWebhookShoot(
                              row.item._id,
                              row.item.linkWebhookSubmit,
                              token,
                            )
                          "
                          class="btn btn-primary"
                          style="max-width: 300px"
                        >
                          {{ $t('actionList.saveWebhook') }}
                        </button>
                      </div>

                      <label>{{ $t('actionList.required') }}</label>
                    </div>

                    <label>Auth Token:</label>
                    <div
                      class="token"
                      style="margin-bottom: 5px; display: flex"
                    >
                      <input
                        :id="`webhook-shoot-token-${row.index}`"
                        v-model="token"
                        type="text"
                        class="form-control"
                      />
                      <div class="custom-copy">
                        <button
                          :title="`${$t('resend.invitesTable.labels.copy')}`"
                          class="fas fa-copy copy"
                          @click="copyToken(token)"
                        ></button>
                      </div>
                    </div>
                    <b-button
                      style="margin-bottom: 25px"
                      @click="generateNewToken"
                      variant="outline-primary"
                      >Gerar novo token</b-button
                    >

                    <div class="display-flex" style="margin-bottom: 45px">
                      <button
                        @click="
                          testIntegrationShoot(
                            row.item._id,
                            row.item.linkWebhookSubmit,
                            token,
                          )
                        "
                        class="btn btn-primary"
                      >
                        {{ $t('actionList.testIntegration') }}
                      </button>

                      <b-form-checkbox
                        style="margin-top: 10px; margin-left: 10px"
                        :id="`enable-config-${row.index}`"
                        name="check-button"
                        v-model="row.item.activeWebhookSubmit"
                        @change="
                          activateWebhookShoot(
                            row.item._id,
                            row.item.activeWebhookSubmit,
                          )
                        "
                        switch
                      >
                        {{ $t('actionList.habilitIntegration') }}
                      </b-form-checkbox>
                    </div>
                  </div>
                  <p class="align-paragraph">
                    <span>
                      <a
                        @click="showConfigTemplateWpp = !showConfigTemplateWpp"
                      >
                        <i class="table-icons fas fa-link"></i>
                        <span class="text-config">
                          Configurar template whatsapp
                        </span>
                      </a>
                    </span>
                  </p>
                  <div
                    style="margin-bottom: 20px"
                    v-if="showConfigTemplateWpp && row.item.wppConfig"
                  >
                    <div class="row form-group">
                      <div class="col-6">
                        <label>TemplateName</label>
                        <b-form-input
                          placeholder="Enter your name"
                          v-model="row.item.wppConfig.templateName"
                        ></b-form-input>
                      </div>
                      <div class="col-6">
                        <label>langCode</label>
                        <b-form-input
                          placeholder="Enter your name"
                          v-model="row.item.wppConfig.langCode"
                        ></b-form-input>
                      </div>
                      <div class="col-12 mt-2">
                        <label>Key</label>
                        <b-form-input
                          placeholder="Enter your name"
                          v-model="row.item.wppConfig.key"
                        ></b-form-input>
                      </div>
                      <div class="col-12 mt-2">
                        <label>imageLink</label>
                        <b-form-input
                          placeholder="Enter your name"
                          v-model="row.item.wppConfig.imageLink"
                        ></b-form-input>
                      </div>
                      <div class="col-12 mt-2">
                        <div class="mt-2 custom-switch">
                          <input
                            type="checkbox"
                            class="custom-control-input"
                            id="switch-params-wpp"
                            v-model="enableParameters"
                            @change="addNewParameters(row.item)"
                          />
                          <label
                            class="custom-control-label"
                            for="switch-params-wpp"
                          >
                            Parameters
                          </label>
                        </div>
                        <div
                          class="row"
                          v-for="(param, indexParam) in row.item.wppConfig
                            .parameters"
                          :key="indexParam"
                        >
                          <div class="col-5 pl-3 pr-1 pt-2">
                            <b-form-select
                              v-model="param.type"
                              :options="listType"
                              @change="
                                updateListValuesByType(
                                  row.index,
                                  indexParam,
                                  param.type,
                                )
                              "
                            ></b-form-select>
                          </div>
                          <div class="col-5 pl-3 pr-1 pt-2">
                            <b-form-select
                              v-if="
                                param.type !== 'clientName' &&
                                param.type !== 'clientEmail'
                              "
                              v-model="param.value"
                              :options="listValueIndicator"
                            ></b-form-select>
                            <b-form-select
                              v-if="
                                param.type === 'clientName' ||
                                param.type === 'clientEmail'
                              "
                              v-model="param.value"
                              :options="listValuesWithEmailAndName"
                              :disabled="
                                param.type === 'clientName' ||
                                param.type === 'clientEmail'
                                  ? true
                                  : false
                              "
                            ></b-form-select>
                          </div>
                          <div class="col-2 pl-3 pr-1 pt-2 display-flex">
                            <button
                              :id="`item-add-${row.item._id}`"
                              class="btn btn-outline-primary"
                              @click="addNewIndicators(row.item)"
                            >
                              <font-awesome-icon :icon="['fas', 'plus']" />
                            </button>
                            <button
                              :id="`item-remove-${row.item._id}`"
                              class="btn btn-outline-danger"
                              @click="removeIndicator(row.item, indexParam)"
                            >
                              <font-awesome-icon :icon="['fas', 'trash-alt']" />
                            </button>
                          </div>
                        </div>

                        <b-button
                          v-b-modal="`modal-${row.item._id}-test-template`"
                          class="mt-2"
                          variant="outline-primary"
                          >Testar template</b-button
                        >
                        <b-button
                          class="mt-2"
                          variant="primary"
                          @click="saveIndicators(row.item)"
                          >Salvar</b-button
                        >
                      </div>
                    </div>

                    <b-modal
                      :id="`modal-${row.item._id}-test-template`"
                      title="Testar template"
                      @ok="testTemplateWpp(row.item.wppConfig)"
                    >
                      <label>Digite o número do seu whatsapp</label>
                      <b-form-input
                        v-model="numberTemplateWpp"
                        placeholder="55195555555"
                      ></b-form-input>

                      <template #modal-ok> Testar </template>
                    </b-modal>
                  </div>

                  <p class="align-paragraph">
                    <span>
                      <a @click="showActionEmail = !showActionEmail">
                        <i class="table-icons fas fa-envelope"></i>
                        <span class="text-config">
                          {{ $t('actionList.registerEmail') }}
                        </span>
                      </a>
                    </span>
                  </p>

                  <p class="align-paragraph">
                    <span
                      v-if="
                        row.item.surveyType === 'resurvey' &&
                        userProfile.permissions.actions.edit
                      "
                    >
                      <a v-b-modal="`resurvey-modal-${row.index}`">
                        <i class="table-icons fas fa-cog"></i
                        ><span class="text-config">
                          Configurações de repesquisa
                        </span>
                      </a>
                    </span>
                  </p>

                  <div v-if="showActionEmail">
                    <p class="align-paragraph"></p>
                    <div class="form-group">
                      <label>
                        {{ $t('actionList.actionDetails.labels.email') }}:
                      </label>
                      <div style="display: flex">
                        <input
                          type="text"
                          class="form-control"
                          v-model="row.item.email.email"
                        />
                        <button
                          @click="saveActionEmail(row.item)"
                          class="btn btn-outline-primary"
                        >
                          {{ $t('genericMessages.save') }}
                        </button>
                      </div>
                    </div>
                  </div>
                </b-modal>
              </div>

              <b-modal
                :id="`resurvey-modal-${row.index}`"
                :title="$t('actionList.resurveyModal.title')"
                size="xl"
                :ok-title="$t('actionList.resurveyModal.saveButton')"
                :cancel-title="$t('actionList.resurveyModal.cancelButton')"
                @show="showModalInfo(row.item)"
                @ok="saveResurveyGrades(row.item._id)"
              >
                <div class="row">
                  <div class="col-12">
                    <p>
                      {{ $t('actionList.resurveyModal.description') }}
                    </p>
                  </div>
                </div>
                <div class="row">
                  <div class="col-6">
                    <p>
                      {{ $t('actionList.resurveyModal.gradesDescription') }}
                    </p>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12">
                    <div style="display: flex">
                      <h5>Configuração do disparo da repesquisa</h5>
                      <b-form-checkbox
                        v-model="checked"
                        style="margin-left: 10px"
                        :checked="checked"
                        switch
                      >
                      </b-form-checkbox>
                    </div>
                    <div class="row">
                      <div class="col-12">
                        <div class="row">
                          <div class="col-4 mt-2">
                            <div class="form-group">
                              <label>Dias para envio:</label>
                              <input
                                type="number"
                                class="form-control"
                                v-model="days"
                                :disabled="disabledInput"
                              />
                            </div>
                          </div>
                          <div class="col-4 mt-2">
                            <div class="form-group">
                              <label for="time">Horário:</label>
                              <b-form-select
                                id="time"
                                v-model="timeSelected"
                                :disabled="disabledInput"
                                :options="timeOptions"
                              ></b-form-select>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <hr />
                <h5>Selecione a ação gatilho para repesquisa</h5>
                <div class="row">
                  <div class="col-6 mt-3">
                    <div class="search-actions">
                      <input
                        type="text"
                        class="form-control"
                        id="input-action"
                        :placeholder="$t('actionList.searchInput')"
                        v-model="modal.filter"
                        @keyup="searchByModalFilter(row.item)"
                      />
                      <button
                        @click="cleanModalFilter(row.item)"
                        class="btn btn-outline-primary"
                      >
                        {{ $t('actionList.searchButton') }}
                      </button>
                    </div>
                  </div>
                  <div class="col-6">
                    <div class="grade-checkbox-section mr-3">
                      <div
                        v-for="(item, i) in modal.grades"
                        :key="item.grade"
                        class="grade-checkbox"
                      >
                        <b-form-checkbox
                          :id="`grade-${i}`"
                          v-model="item.value"
                          class="check-alert-modal ml-3"
                        >
                        </b-form-checkbox>
                        <label :for="`grade-${i}`" class="ml-1">{{
                          item.grade
                        }}</label>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12">
                    <b-table
                      striped
                      id="manual-table"
                      class="mt-4"
                      :fields="modal.fields"
                      :items="modal.items"
                      :busy="modal.loading"
                    >
                      <template v-slot:table-busy>
                        <div class="text-center text-primary my-2">
                          <b-spinner
                            type="grow"
                            class="align-middle"
                          ></b-spinner>
                        </div>
                      </template>
                      <template v-slot:cell(groupIdList)="modalRow">
                        <!-- {{
                          modalRow.item.groupId
                            ? modalRow.item.groupId.name
                            : $t('actionList.actionTable.noClassifiedLabel')
                        }} -->
                        <div
                          v-for="item in modalRow.item.groupIdList"
                          :key="item.groupIdList"
                        >
                          <label class="ml-1">{{
                            modalRow.item.groupIdList
                              ? item.name
                              : $t('actionList.actionTable.noClassifiedLabel')
                          }}</label>
                        </div>
                      </template>
                      <template v-slot:cell(createdAt)="modalRow">{{
                        dateFormat(new Date(modalRow.item.createdAt))
                      }}</template>
                      <template v-slot:cell(status)="modalRow">
                        <b-form-checkbox
                          @change="
                            associateResurveyId(
                              $event,
                              row.item._id,
                              modalRow.item._id,
                            )
                          "
                          :checked="
                            verifyResurveyAssociated(
                              modalRow.item.resurveyId,
                              row.item._id,
                            )
                          "
                          switch
                        >
                        </b-form-checkbox>
                      </template>
                    </b-table>
                    <div
                      v-show="!modal.loading"
                      class="pagination-area-action-list"
                    >
                      <paginate
                        v-model="modal.page"
                        :pageCount="Math.ceil(modal.totalItems / modal.limit)"
                        :clickHandler="updateTableModal"
                        :prevText="'‹'"
                        :nextText="'›'"
                        :activeClass="'active'"
                        :containerClass="'pagination b-pagination'"
                        :pageClass="'page-item'"
                      ></paginate>
                    </div>
                  </div>
                </div>
              </b-modal>
            </template>
          </b-table>
          <div v-show="!loading" class="footer">
            <div v-show="!loading" class="pagination-area-action-list" v-if="userProfile.permissions.actions.menu_list">
              <paginate
              v-model="page"
              :pageCount="Math.ceil(totalActionList / this.limit)"
              :clickHandler="updateTable"
              :prevText="'‹'"
              :nextText="'›'"
              :activeClass="'active'"
              :containerClass="'pagination b-pagination'"
              :pageClass="'page-item'"
            ></paginate>
            </div>
            <div class="row d-flex justify-content-end mr-2">
              <div class="mr-3">
                <div
                  v-if="loadingDownloadIconCsv"
                  class="text-center text-primary my-2"
                >
                  <b-spinner type="grow" class="align-middle"></b-spinner>
                </div>
                <a v-else :title="$t('clients.exportCsv')">
                  <b-dropdown
                    variant="link"
                    toggle-class="text-decoration-none dropdown-csv"
                    no-caret
                  >
                    <template #button-content>
                      <span class="xls">
                        <i class="fas fa-file-csv"></i>
                      </span>
                    </template>
                    <b-dropdown-item @click="exportData('csv', 'comma')">{{
                      $t('clients.csvCommaSeparator')
                    }}</b-dropdown-item>
                    <b-dropdown-item @click="exportData('csv', 'pipe')">{{
                      $t('clients.csvPipeSeparator')
                    }}</b-dropdown-item>
                  </b-dropdown>
                </a>
              </div>
              <div>
                <div
                  v-if="loadingDownloadIconXls"
                  class="text-center text-primary my-2"
                >
                  <b-spinner type="grow" class="align-middle"></b-spinner>
                </div>
                <a
                  v-else
                  @click="exportData('xlsx', null)"
                  :title="$t('clients.exportXls')"
                >
                  <span class="xls">
                    <i class="fas fa-file-excel"></i>
                  </span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// eslint-disable-next-line import/no-extraneous-dependencies
import { v4 as uuidv4 } from 'uuid';
import { mapState, mapActions } from 'vuex';
import { format, lightFormat } from 'date-fns';
import { mapFields } from 'vuex-map-fields';
import pt from 'date-fns/locale/pt';
import Paginate from 'vuejs-paginate';
import swal from 'sweetalert';
import DropdownGroup from '../../molecules/dropdown-group';
import DropdownCategoryGroups from '../../molecules/dropdown-category-groups';
import DropdownTagGroups from '../../molecules/dropdown-tags-groups';
import dashboardBar from '../../molecules/dashboard-bar';
import api from '../../../services/api';

export default {
  data: () => ({
    groupSelected: '',
    loading: true,
    limit: 5,
    userAuth: null,
    filter: null,
    typingTimer: null,
    fields: [],
    sortBy: null,
    sortDesc: null,
    days: 0,
    timeSelected: '12:00',
    timeOptions: [
      { value: '00:00', text: '00:00' },
      { value: '00:30', text: '00:30' },
      { value: '01:00', text: '01:00' },
      { value: '01:30', text: '01:30' },
      { value: '02:00', text: '02:00' },
      { value: '02:30', text: '02:30' },
      { value: '03:00', text: '03:00' },
      { value: '03:30', text: '03:30' },
      { value: '04:00', text: '04:00' },
      { value: '04:30', text: '04:30' },
      { value: '05:00', text: '05:00' },
      { value: '05:30', text: '05:30' },
      { value: '06:00', text: '06:00' },
      { value: '06:30', text: '06:30' },
      { value: '07:00', text: '07:00' },
      { value: '07:30', text: '07:30' },
      { value: '08:00', text: '08:00' },
      { value: '08:30', text: '08:30' },
      { value: '09:00', text: '09:00' },
      { value: '09:30', text: '09:30' },
      { value: '10:00', text: '10:00' },
      { value: '10:30', text: '10:30' },
      { value: '11:00', text: '11:00' },
      { value: '11:30', text: '11:30' },
      { value: '12:00', text: '12:00' },
      { value: '12:30', text: '12:30' },
      { value: '13:00', text: '13:00' },
      { value: '13:30', text: '13:30' },
      { value: '14:00', text: '14:00' },
      { value: '14:30', text: '14:30' },
      { value: '15:00', text: '15:00' },
      { value: '15:30', text: '15:30' },
      { value: '16:00', text: '16:00' },
      { value: '16:30', text: '16:30' },
      { value: '17:00', text: '17:00' },
      { value: '17:30', text: '17:30' },
      { value: '18:00', text: '18:00' },
      { value: '18:30', text: '18:30' },
      { value: '19:00', text: '19:00' },
      { value: '19:30', text: '19:30' },
      { value: '20:00', text: '20:00' },
      { value: '20:30', text: '20:30' },
      { value: '21:00', text: '21:00' },
      { value: '21:30', text: '21:30' },
      { value: '22:00', text: '22:00' },
      { value: '22:30', text: '22:30' },
      { value: '23:00', text: '23:00' },
      { value: '23:30', text: '23:30' },
    ],
    modal: {
      loading: false,
      filter: null,
      page: 1,
      limit: 10,
      grades: [
        { grade: 0, value: false },
        { grade: 1, value: false },
        { grade: 2, value: false },
        { grade: 3, value: false },
        { grade: 4, value: false },
        { grade: 5, value: false },
        { grade: 6, value: false },
        { grade: 7, value: false },
        { grade: 8, value: false },
        { grade: 9, value: false },
        { grade: 10, value: false },
      ],
      fields: [],
      items: [],
      totalItems: 0,
    },
    disabledInput: true,
    checked: true,
    showActionEmail: false,
    showWebhook: false,
    showWebhookShoot: false,
    showConfigTemplateWpp: false,
    token: null,
    listType: [
      { value: null, text: 'Selecione' },
      { value: 'clientName', text: 'clientName' },
      { value: 'clientEmail', text: 'clientEmail' },
      { value: 'indicatorColumn', text: 'indicatorColumn' },
    ],
    listValueIndicator: [],
    enableParameters: false,
    numberTemplateWpp: '',
    listValuesWithEmailAndName: [],
    loadingDownloadIconXls: false,
    loadingDownloadIconCsv: false,
  }),

  components: {
    Paginate,
    DropdownGroup,
    DropdownCategoryGroups,
    dashboardBar,
    DropdownTagGroups,
  },

  computed: {
    ...mapState('actions', [
      'actionList',
      'action',
      'totalActionList',
      'actionGroup',
      'actionGroupData',
    ]),
    ...mapState('authentication', ['userProfile']),
    ...mapState('companies', ['myMetric']),
    ...mapFields('actions', ['page', 'fromDetails', 'isEditAction']),
    ...mapState('calendar', ['calendarDates']),
    ...mapState('branches', ['myIh1', 'myHierarchies1']),
  },

  async mounted() {
    await this.updateTable();
  },

  created() {
    this.modal.fields = [
      { key: 'controlId', label: 'ID' },
      {
        key: 'name',
        label: this.$t('actionList.resurveyModal.tableLabels.name'),
      },
      {
        key: 'groupIdList',
        label: this.$t('actionList.resurveyModal.tableLabels.actionGroup'),
      },
      {
        key: 'createdAt',
        label: this.$t('actionList.resurveyModal.tableLabels.createdAt'),
      },
      {
        key: 'channel',
        label: this.$t('actionList.resurveyModal.tableLabels.channel'),
      },
      {
        key: 'status',
        label: this.$t('actionList.resurveyModal.tableLabels.resurvey'),
      },
    ];

    this.fields = [
      { key: 'controlId', label: this.$t('actionList.actionTable.labels.id') },
      {
        key: 'name',
        label: this.$t('actionList.actionTable.labels.action'),
        sortable: true,
      },
      {
        key: 'groupIdList',
        label: this.$t('actionList.actionTable.labels.group'),
        sortable: true,
      },
      {
        key: 'type',
        label: this.$t('actionList.actionTable.labels.type'),
        sortable: true,
      },
      {
        key: 'createdAt',
        label: this.$t('actionList.actionTable.labels.date'),
        sortable: true,
      },
      {
        key: 'channel',
        label: this.$t('actionList.actionTable.labels.channel'),
      },
      {
        key: 'clientsCount',
        label: this.$t('actionList.actionTable.labels.registers'),
      },
      {
        key: 'invites',
        label: this.$t('actionList.actionTable.labels.valids'),
      },
      {
        key: 'failures',
        label: this.$t('actionList.actionTable.labels.failures'),
      },
      {
        key: 'quarentine',
        label: this.$t('actionList.actionTable.labels.quarentine'),
      },
      {
        key: 'answers',
        label: this.$t('actionList.actionTable.labels.answers'),
      },
      {
        key: 'status',
        label: this.$t('actionList.actionTable.labels.status'),
      },
      {
        key: 'options',
        label: this.$t('actionList.actionTable.labels.options'),
      },
    ];

    this.userAuth = JSON.parse(localStorage.getItem('_indecx'));

    if (!this.userProfile.permissions.actions.menu_list) {
      this.$router.push(`/dashboard/${this.userAuth.companyId}`);
    }

    if (this.fromDetails) {
      this.fromDetails = false;
    } else {
      this.page = 1;
    }
  },

  watch: {
    myIh1: function watchMyIh1() {
      this.updateTable();
    },

    myHierarchies1: function watchMyHierarchies1() {
      this.updateTable();
    },

    checked: function watchChecked(value) {
      if (value) {
        this.disabledInput = false;
      } else {
        this.disabledInput = true;
      }
    },
    calendarDates: function watchCalendarDates() {
      this.page = 1;
      this.updateTable();
    },
  },

  methods: {
    ...mapActions('actions', [
      'setActionList',
      'setAction',
      'removeAction',
      'setCloneAction',
      'getAllActions',
    ]),
    ...mapActions('companies', ['recalculateNps']),
    ...mapActions('treatments', ['setAnswersAllActions', 'updateStatus']),

    updateListValuesByType(indexAction, indexWppConfig, typeSelected) {
      if (typeSelected === 'clientEmail') {
        this.actionList[indexAction].wppConfig.parameters[
          indexWppConfig
        ].value = 'clientEmail';
      } else if (typeSelected === 'clientName') {
        this.actionList[indexAction].wppConfig.parameters[
          indexWppConfig
        ].value = 'clientName';
      } else {
        this.actionList[indexAction].wppConfig.parameters[
          indexWppConfig
        ].value = null;
      }
    },

    async exportData(type, csvSeparator) {
      // checks if the total docs to download is greater then 500k
      if (this.totalActionList > 500000) {
        swal({
          title: this.$t('scheduling.messages.maxDownloadError.title'),
          text: this.$t('scheduling.messages.maxDownloadError.description'),
          icon: 'warning',
        });
        return;
      }

      if (type === 'xlsx') {
        this.loadingDownloadIconXls = true;
      } else {
        this.loadingDownloadIconCsv = true;
      }

      if (csvSeparator === 'comma') {
        csvSeparator = ',';
      } else if (csvSeparator === 'pipe') {
        csvSeparator = '|';
      }

      this.apiServerlessDownloads = process.env.API_SERVERLESS_DOWNLOADS;

      try {
        await api.get(
          `${this.apiServerlessDownloads}/download/action/${this.userAuth.companyId}/${type}`,
          {
            headers: {
              'x-access-token': this.userAuth.token,
            },
            params: {
              startDate: this.calendarDates.start,
              endDate: this.calendarDates.end,
              groupId: this.actionGroupData._id,
              metric: this.myMetric,
              filter: this.search,
              csvSeparator,
            },
          },
        );

        swal({
          title: this.$t('clients.messages.export.title'),
          text: this.$t('clients.messages.export.description'),
          icon: 'success',
        });
      } catch (error) {
        swal({
          title: this.$t('clients.messages.error.title'),
          text: error.response.data.message,
          icon: 'error',
        });
      }

      if (type === 'xlsx') {
        this.loadingDownloadIconXls = false;
      } else {
        this.loadingDownloadIconCsv = false;
      }
    },

    async testTemplateWpp(wppConfig) {
      let completedNotFields = false;

      if (!wppConfig.templateName) {
        completedNotFields = true;
        swal({
          title: 'Opss!',
          text: 'Preencha o nome do template!',
          icon: 'warning',
          buttons: 'Ok',
        });
      }

      if (!wppConfig.key) {
        completedNotFields = true;
        swal({
          title: 'Opss!',
          text: 'Preencha o campo de key',
          icon: 'warning',
          buttons: 'Ok',
        });
      }

      if (!wppConfig.langCode) {
        completedNotFields = true;
        swal({
          title: 'Opss!',
          text: 'Preencha o campo de langCode',
          icon: 'warning',
          buttons: 'Ok',
        });
      }

      if (!wppConfig.imageLink) {
        completedNotFields = true;
        swal({
          title: 'Opss!',
          text: 'Preencha o campo de imageLink',
          icon: 'warning',
          buttons: 'Ok',
        });
      }

      if (!completedNotFields) {
        await api.post(
          'test-config-template-wpp',
          {
            wppConfig,
            phone: this.numberTemplateWpp,
          },
          {
            headers: { 'x-access-token': this.userAuth.token },
          },
        );

        this.numberTemplateWpp = '';

        swal({
          title: 'Mensagem enviada!',
          text: 'Verifique seu whatsapp',
          icon: 'success',
          buttons: 'Ok',
        });
      }
    },

    async saveIndicators(item) {
      let completedNotFields = false;

      if (!item.wppConfig.templateName) {
        completedNotFields = true;
        swal({
          title: 'Opss!',
          text: 'Preencha o nome do template!',
          icon: 'warning',
          buttons: 'Ok',
        });
      }

      if (!item.wppConfig.key) {
        completedNotFields = true;
        swal({
          title: 'Opss!',
          text: 'Preencha o campo de key',
          icon: 'warning',
          buttons: 'Ok',
        });
      }

      if (!item.wppConfig.langCode) {
        completedNotFields = true;
        swal({
          title: 'Opss!',
          text: 'Preencha o campo de langCode',
          icon: 'warning',
          buttons: 'Ok',
        });
      }

      if (!item.wppConfig.imageLink) {
        completedNotFields = true;
        swal({
          title: 'Opss!',
          text: 'Preencha o campo de imageLink',
          icon: 'warning',
          buttons: 'Ok',
        });
      }

      let existFieldEmpty = false;

      if (item.wppConfig.parameters && item.wppConfig.parameters.length > 0) {
        for (let a = 0; a < item.wppConfig.parameters.length; a += 1) {
          if (item.wppConfig.parameters[a].type &&
              item.wppConfig.parameters[a].type === undefined || !item.wppConfig.parameters[a].type &&
              item.wppConfig.parameters[a].type !== 'clientName' &&
              item.wppConfig.parameters[a].type !== 'clientEmail' &&
              item.wppConfig.parameters[a].type !== 'indicatorColumn'
          ) {
            existFieldEmpty = true;
            break;
          }
        }
      }

      if (existFieldEmpty) {
        swal({
          title: 'Opss!',
          text: 'Selecione um valor para o parâmetro!',
          icon: 'warning',
          buttons: 'Ok',
        });
      }

      if (!existFieldEmpty && !completedNotFields) {
        await api
          .post(
            `config-template-wpp/${item._id}`,
            {
              wppConfig: item.wppConfig,
            },
            {
              headers: { 'x-access-token': this.userAuth.token },
            },
          )
          .then((resp) => {
            if (resp.data) {
              swal({
                title: 'Sucesso!',
                text: 'Salvo com sucesso!',
                icon: 'success',
                buttons: 'Ok',
              });
            }
          });
      }
    },

    removeIndicator(item, indexIndicator) {
      item.wppConfig.parameters.splice(indexIndicator, 1);
    },

    addNewIndicators(item) {
      item.wppConfig.parameters.push({
        type: null,
        value: null,
      });
    },

    async getIndicatorValueByType(actionId) {
      const response = await api.get(`get-indicators-by-action/${actionId}`, {
        headers: {
          'x-access-token': this.userAuth.token,
        },
      });

      this.listValueIndicator = [];

      this.listValueIndicator.push({ value: null, text: 'Selecione' });

      this.listValuesWithEmailAndName.push(
        { value: null, text: 'Selecione' },
        { value: 'clientEmail', text: 'Email' },
        { value: 'clientName', text: 'Name' },
      );

      response.data.getIndicatorsAction.forEach((el) => {
        this.listValueIndicator.push({
          value: el.key,
          text: el.key,
        });
      });
    },

    addNewParameters(item) {
      if (this.enableParameters && item.wppConfig.parameters.length === 0) {
        item.wppConfig.parameters = [
          {
            type: null,
            value: null,
          },
        ];
      }
    },

    openModal(item) {
      this.token = item.tokenWebhookSubmit ? item.tokenWebhookSubmit : '';
      this.getIndicatorValueByType(item._id);
      if (
        item.wppConfig &&
        item.wppConfig.parameters &&
        item.wppConfig.parameters.length > 0
      ) {
        this.enableParameters = true;
      } else {
        this.enableParameters = false;
      }
    },

    generateNewToken() {
      this.token = uuidv4();
    },

    copyToken(text) {
      navigator.clipboard.writeText(text);
      this.$bvToast.toast('O Token foi copiado para a área de transferência.', {
        title: 'Token copiado',
        autoHideDelay: 3000,
        variant: 'success',
        solid: true,
      });
    },

    async activateWebhookShoot(actionId, active) {
      await api.post(
        '/enable-webhook-action-submit',
        {
          companyId: this.$store.state.companies.company._id,
          actionId,
          active,
        },
        {
          headers: { 'x-access-token': this.userAuth.token },
        },
      );

      swal({
        title: 'Sucesso!',
        text: 'Alteração salva com sucesso!',
        icon: 'success',
        buttons: 'Ok',
      });

      this.updateTable();
    },

    async activateWebhook(actionId, active) {
      await api.post(
        '/enable-webhook-action',
        {
          companyId: this.$store.state.companies.company._id,
          actionId,
          active,
        },
        {
          headers: { 'x-access-token': this.userAuth.token },
        },
      );

      swal({
        title: 'Sucesso!',
        text: 'Alteração salva com sucesso!',
        icon: 'success',
        buttons: 'Ok',
      });
    },

    async saveWebhook(actionId, url) {
      if (url === '') {
        swal({
          title: 'Dados incorretos',
          text: 'Digite uma url',
          icon: 'warning',
          buttons: 'Ok',
        });
      } else {
        await api.post(
          '/webhooks-action',
          {
            link: url,
            companyId: this.$store.state.companies.company._id,
            type: 'answer',
            actionId,
          },
          {
            headers: { 'x-access-token': this.userAuth.token },
          },
        );

        swal({
          title: 'Sucesso!',
          text: 'Alteração salva com sucesso!',
          icon: 'success',
          buttons: 'Ok',
        });
      }
    },

    async saveWebhookShoot(actionId, url, token) {
      if (url === '') {
        swal({
          title: 'Dados incorretos',
          text: 'Digite uma url',
          icon: 'warning',
          buttons: 'Ok',
        });
      } else {
        await api.post(
          '/webhooks-action-submit',
          {
            link: url,
            companyId: this.$store.state.companies.company._id,
            type: 'submit',
            actionId,
            token,
          },
          {
            headers: { 'x-access-token': this.userAuth.token },
          },
        );

        swal({
          title: 'Sucesso!',
          text: 'Alteração salva com sucesso!',
          icon: 'success',
          buttons: 'Ok',
        });

        this.updateTable();
      }
    },

    async testIntegrationAnswer(actionId, url) {
      if (url === '') {
        swal({
          title: 'Dados incorretos',
          text: 'Digite uma url',
          icon: 'warning',
          buttons: 'Ok',
        });
      } else {
        await api.post(
          '/webhooks-action/link/collect',
          {
            link: url,
            companyId: this.$store.state.companies.company._id,
            actionId,
          },
          {
            headers: { 'x-access-token': this.userAuth.token },
          },
        );
        swal({
          text: 'Sucesso!',
          icon: 'success',
          buttons: 'Ok',
        });
      }
    },

    async testIntegrationShoot(actionId, url) {
      if (url === '') {
        swal({
          title: 'Dados incorretos',
          text: 'Digite uma url',
          icon: 'warning',
          buttons: 'Ok',
        });
      } else {
        await api.post(
          '/webhooks-action/link/collect-submit',
          {
            link: url,
            companyId: this.$store.state.companies.company._id,
            actionId,
            token: this.token,
          },
          {
            headers: { 'x-access-token': this.userAuth.token },
          },
        );
        swal({
          text: 'Sucesso!',
          icon: 'success',
          buttons: 'Ok',
        });
      }
    },

    async saveResurveyGrades(actionId) {
      try {
        await api.put(
          `resurvey/grades/${actionId}`,
          {
            resurveyGrades: this.modal.grades,
          },
          {
            headers: {
              'x-access-token': this.userAuth.token,
            },
          },
        );

        await api.put(
          `/resurvey/research-config`,
          {
            days: this.days,
            time: this.timeSelected,
            active: this.checked,
            resurveyId: actionId,
          },
          {
            headers: {
              'x-access-token': this.userAuth.token,
            },
          },
        );

        swal({
          title: this.$t('genericMessages.success.title'),
          text: this.$t('genericMessages.success.description'),
          icon: 'success',
        });

        this.updateTable();
      } catch (error) {
        swal({
          title: this.$t('genericMessages.error.title'),
          text: this.$t('genericMessages.error.description'),
          icon: 'error',
        });
      }
    },

    async updateConfig(checked, id) {
      try {
        await api.put(
          `/resurvey/research-config`,
          {
            days: this.days,
            time: this.timeSelected,
            active: checked,
            resurveyId: id,
          },
          {
            headers: {
              'x-access-token': this.userAuth.token,
            },
          },
        );
      } catch (error) {
        swal({
          title: this.$t('genericMessages.error.title'),
          text: this.$t('genericMessages.error.description'),
          icon: 'error',
        });
      }
    },

    async updateTable() {
      this.loading = true;

      await this.setActionList({
        token: this.userAuth.token,
        companyId: this.userAuth.companyId,
        query: {
          page: this.page,
          limit: this.limit,
          filter: this.filter,
          groupId: this.$route.query.groupId,
          sortBy: this.sortBy,
          sortDesc: this.sortDesc,
          startDate: this.calendarDates.start ? this.calendarDates.start : null,
          endDate: this.calendarDates.end ? this.calendarDates.end : null,
          ih1:
            this.myHierarchies1.length === 0
              ? null
              : this.myHierarchies1.join(','),
        },
      });

      this.loading = false;
    },

    async updateTableModal() {
      this.modal.loading = true;

      const response = await api.get(`actions/${this.userAuth.companyId}`, {
        params: {
          page: this.modal.page,
          limit: this.modal.limit,
          filter: this.modal.filter,
          surveyType: 'survey',
          noDetails: true,
        },
        headers: {
          'x-access-token': this.userAuth.token,
        },
      });

      this.modal.items = response.data.actions;

      this.modal.totalItems = response.data.total;

      this.modal.loading = false;
    },

    async showModalInfo(action) {
      this.modal.loading = true;

      const resurveyGrades = action.resurveyGrades || [];

      this.modal.grades.forEach((item) => {
        const found = resurveyGrades.find(
          (el) => el.grade === item.grade && el.value,
        );

        if (found) {
          item.value = true;
        } else {
          item.value = false;
        }
      });

      const response = await api.get(`actions/${this.userAuth.companyId}`, {
        params: {
          page: this.modal.page,
          limit: this.modal.limit,
          filter: this.modal.filter,
          surveyType: 'survey',
          noDetails: true,
        },
        headers: {
          'x-access-token': this.userAuth.token,
        },
      });

      this.modal.items = response.data.actions;

      this.modal.totalItems = response.data.total;

      this.modal.loading = false;

      await api
        .get(`/resurvey/research-config/${action._id}`, {
          headers: {
            'x-access-token': this.userAuth.token,
          },
        })
        .then((data) => {
          if (data.data.resurveyScheduling.active) {
            this.disabledInput = false;
          } else {
            this.disabledInput = true;
          }

          this.checked = data.data.resurveyScheduling.active;
          this.days = data.data.resurveyScheduling.days;
          this.timeSelected = data.data.resurveyScheduling.time;
        });
    },

    searchByModalFilter(item) {
      if (this.modal.filter !== '' && this.modal.filter !== null) {
        this.modal.page = 1;
        clearTimeout(this.typingTimer);
        this.typingTimer = setTimeout(this.showModalInfo(item), 600);
      }

      if (this.modal.filter === '') {
        this.modal.filter = null;
        this.modal.page = 1;
        this.showModalInfo();
      }
    },

    cleanModalFilter(item) {
      this.modal.filter = null;
      this.showModalInfo(item);
    },

    handleSorting(ctx) {
      this.sortBy = ctx.sortBy;
      this.sortDesc = ctx.sortDesc;
      this.updateTable();
    },

    async associateResurveyId(checkEnabled, resurveyId, actionId) {
      try {
        await api.put(
          `resurvey/associate/${actionId}`,
          { resurveyId: checkEnabled ? resurveyId : null },
          {
            headers: {
              'x-access-token': this.userAuth.token,
            },
          },
        );

        swal({
          title: this.$t('genericMessages.success.title'),
          text: this.$t('genericMessages.success.description'),
          icon: 'success',
        });
      } catch (error) {
        swal({
          title: this.$t('genericMessages.error.title'),
          text: this.$t('genericMessages.error.description'),
          icon: 'error',
        });
      }
    },

    verifyResurveyAssociated(resurveyId, actionId) {
      if (resurveyId === actionId) {
        return true;
      }

      return false;
    },

    searchByFilter() {
      if (this.filter !== '' && this.filter !== null) {
        this.page = 1;
        clearTimeout(this.typingTimer);
        this.typingTimer = setTimeout(this.updateTable, 600);
      }

      if (this.filter === '') {
        this.filter = null;
        this.page = 1;
        this.updateTable();
      }
    },

    cleanFilter() {
      this.filter = null;
      this.updateTable();
    },

    remove(actionId) {
      swal({
        title: this.$t('actionList.messages.remove.title'),
        text: this.$t('actionList.messages.remove.description'),
        icon: 'info',
        buttons: [
          this.$t('actionList.messages.remove.cancelLabel'),
          this.$t('actionList.messages.remove.okLabel'),
        ],
        dangerMode: true,
      }).then((deleted) => {
        if (deleted) {
          this.removeAction({
            token: this.userAuth.token,
            companyId: this.userAuth.companyId,
            actionId,
            page: this.atualPage,
          });

          swal({
            title: this.$t('actionList.messages.removeOk.title'),
            text: this.$t('actionList.messages.removeOk.description'),
            icon: 'success',
          }).then(() => {
            this.updateTable();

            this.getAllActions({
              companyId: this.userAuth.companyId,
              token: this.userAuth.token,
              params: {
                type: this.myMetric,
                groupId: this.actionGroupData._id,
              },
            });
          });
        }
      });
    },

    dateFormat(param) {
      const date = format(param, 'dd/MM/yyyy HH:mm', { locale: pt });
      return date;
    },

    showSubmitDate(item) {
      if (item.details.submitDate !== undefined) {
        const day = lightFormat(
          new Date(item.details.submitDate),
          'dd/MM/yyyy',
        );
        const hour = lightFormat(new Date(item.details.submitDate), 'H:mm');

        if (item.status === 'enviado') {
          return `${this.$t('actionList.executed')} ${day}  ${hour}`;
        }

        if (item.status === 'agendado') {
          return `${this.$t('actionList.scheduled')} ${day}  ${hour}`;
        }

        return `${day}  ${hour}`;
      }

      return '';
    },

    // resubmit(actionId) {
    //   this.setAction({
    //     id: actionId,
    //     token: this.userAuth.token,
    //   }).then(() => {
    //     this.$store.state.actions.isResubmit = true;
    //     this.$router.push('actions');
    //   });
    // },

    // editAction(actionId) {
    //   this.setAction({
    //     id: actionId,
    //     token: this.userAuth.token,
    //   }).then(() => {
    //     this.$router.push('/actions');
    //     this.isEditAction = true;
    //   });
    // },

    cloneAction(actionId) {
      swal({
        title: this.$t('actionList.messages.clone.title'),
        text: this.$t('actionList.messages.clone.description'),
        icon: 'info',
        buttons: [
          this.$t('actionList.messages.clone.cancelLabel'),
          this.$t('actionList.messages.clone.okLabel'),
        ],
        dangerMode: true,
      }).then(async (clone) => {
        if (clone) {
          await this.setCloneAction({
            id: actionId,
            token: this.userAuth.token,
          });

          swal({
            title: this.$t('actionList.messages.cloneOk.title'),
            text: this.$t('actionList.messages.cloneOk.description'),
            icon: 'success',
          }).then(() => {
            this.page = 1;

            this.updateTable();

            this.getAllActions({
              companyId: this.userAuth.companyId,
              token: this.userAuth.token,
              params: {
                type: this.myMetric,
                groupId: this.actionGroupData._id,
              },
            });
          });
        }
      });
    },
    async saveActionEmail(item) {
      await api.put(
        `/action/${item._id}`,
        { email: item.email },
        { headers: { 'x-access-token': this.userAuth.token } },
      );
      swal({
        title: this.$t('genericMessages.success.title'),
        text: this.$t('genericMessages.success.description'),
        icon: 'success',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/themes/default/variables.scss';
.custom-copy {
  text-align: right;
  justify-content: right;
  display: flex;
  width: 50px;
}

.copy {
  color: #4ce595;
  background-color: transparent;
  border: 1px solid #4ce595;
  padding-right: 10px;
  padding-left: 10px;
  border-radius: 5px;
}

.align-paragraph {
  margin-left: 8px;
  color: #606060;
  font-size: 1.2em;
  -webkit-box-shadow: none;
  box-shadow: none;
}

.text-config:hover {
  color: #4ce595;
  cursor: pointer;
}

.wrapper {
  padding: 8px;
}

.body {
  background-color: $white;
  padding: 16px;
  border: 1px solid $gray-light;
  border-radius: 4px;
}

.body h2 {
  text-transform: uppercase;
  font-size: 21px;
  font-weight: 100;
  color: $gray;
}

.header {
  display: flex;
  flex-direction: row;

  .header-icon {
    float: left;
    font-size: 12px;
    margin-top: 4px;
    padding-right: 12px;
    color: $gray;
  }

  .refresh-icon {
    margin-top: 1px;
    color: $primary;
    cursor: pointer;
    &:hover {
      color: green;
    }
  }
}

.search-actions {
  display: flex;
  flex-direction: row;

  input {
    border-bottom-right-radius: 0;
    border-top-right-radius: 0;
  }

  button {
    border-bottom-left-radius: 0;
    border-top-left-radius: 0;
  }
}

.flag-status {
  padding: 0.2rem;
  font-size: 0.8rem;
  border-radius: 0.25rem;
  color: $white;
  width: 100px;
  text-align: center;
  opacity: 0.9;
  text-transform: uppercase;
}

.flag-status-criado {
  background-color: #4635dc;
}

.flag-status-enviado {
  background-color: #28a745;
}

.flag-status-agendado {
  background-color: #ffc107;
}

.flag-status-enviando {
  background-color: $primary;
}

.flag-status-processando {
  background-color: #c1c1c1;
}

.flag-status-falha {
  background-color: #eb5555;
}

.flag-status-bloqueado {
  background-color: #333333;
}

.flag-status-importando {
  background-color: #c1c1c1;
}

.flag-status-importado {
  background-color: #7045be;
}

.table-icons {
  color: $primary;
  padding-left: 5px;
  font-size: 20px;
  cursor: pointer;

  svg {
    &:hover {
      color: #46b41e;
    }
  }
}

.pagination-area-action-list >>> a {
  position: relative;
  display: block;
  padding: 0.5rem 0.75rem;
  margin-left: -1px;
  line-height: 1.25;
  color: #4ce595;
  background-color: #fff;
  border: 1px solid #dee2e6;
}

.icons {
  display: flex;
  justify-content: center;
  align-items: center;
}

.group-dropdown {
  width: 300px;
  height: 250px;
  overflow-y: scroll;
}

.survey-type {
  color: rgb(133, 131, 131);
}
</style>

<style>
.display-flex {
  display: flex;
  gap: 10px;
}

.pagination-area-action-list .active a {
  text-decoration: none;
  z-index: 1;
  color: #fff;
  background-color: #4ce595;
  border-color: #4ce595;
}

.pagination-area-action-list a {
  outline: none;
}

.grade-checkbox-section {
  display: flex;
  align-items: center;
}

.grade-checkbox {
  width: 30px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}
.groupListStyle {
  padding: 0px 5px;
  font-weight: bold;
  margin-left: 5px;
}

.footer {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.xls {
  font-size: 35px;
  color: #4ce595;
  cursor: pointer;
}

.xls:hover {
  color: #1dc86e;
}

.dropdown-csv {
  border: 0px;
  padding: 0px;
}
</style>



// WEBPACK FOOTER //
// src/components/organisms/action-list/index.vue